import {
  VuexModule,
  Module,
  Action,
  MutationAction,
  getModule,
  Mutation,
} from "vuex-module-decorators";
import store from "@/store";

import { UsersManagementService } from "@/services/api/users-management.service";
import { AuthModule } from "./auth.module";
import { ContractsModule } from "./contracts.module";
import { BaseModule } from "./base.module";
import i18n from "@/plugins/i18n";

@Module({
  dynamic: true,
  store,
  namespaced: true,
  name: "users-management",
  preserveState: localStorage.getItem("vuex") !== null,
})
export class UsersManagementVuexModule extends VuexModule {
  users: any = [];
  roles: any = [];
  availableContracts: any = [];
  loading: boolean = false;

  @Mutation setUsersLoading(loading: boolean) {
    this.loading = loading;
  }

  @MutationAction
  public async getUsers(params: any) {
    const users = await UsersManagementService.getUsers({
      org_rid: AuthModule.user?.org_rid,
      ...params,
    });
    return { users, loading: false };
  }

  @Action({ rawError: true })
  public async emailAvailable(email: string) {
    if (!email) return;
    email = email.replace("@", "%40");
    const response = await UsersManagementService.checkEmailAvailability(email);
    return !response?.is_exists;
  }

  @MutationAction
  public async getUserRoles() {
    const roles = await UsersManagementService.getUserRoles();
    // making administrator first in the list...
    const admin = roles.find((role: any) => role.role_code === "administrator");
    const index = roles.indexOf(admin);
    roles.splice(index, 1);
    roles.unshift(admin);
    return { roles };
  }

  @Action
  public async getUserContracts(user_rid: string) {
    const temp = await UsersManagementService.getUserContracts(user_rid);
    const allContracts = ContractsModule.contracts;
    // filtering organization contracts to get full information for user contracts
    const userContracts = allContracts.filter(c =>
      temp.some((t: any) => c.contract_rid === t.contract_id),
    );
    return userContracts;
  }

  @MutationAction
  public async getUnassignedContracts() {
    const allContracts = ContractsModule.contracts;
    const org_rid = AuthModule.user?.org_rid as string;
    const assignedContracts = await UsersManagementService.getOrganizationContracts(
      org_rid,
    );

    // filtering organization contracts to contain only unassigned ones
    const availableContracts = allContracts.filter(
      all =>
        !assignedContracts.some(
          (assigned: any) => all.contract_rid === assigned.contract_id,
        ),
    );

    return { availableContracts };
  }

  @Action
  public async addUser(payload: any): Promise<{}> {
    const response = await UsersManagementService.addUser(payload);
    if (!response.successful) {
      BaseModule.showMessage({
        message: i18n.t("profile.users_management.errors.user_add") as string,
        color: "error",
      });
    }
    return response.data;
  }

  @Action({ rawError: true })
  public async editUser(payload: any): Promise<{}> {
    const response = await UsersManagementService.editUser(payload);
    if (!response.successful) {
      BaseModule.showMessage({
        message: i18n.t("profile.users_management.errors.user_edit") as string,
        color: "error",
      });
    }
    return response.data;
  }
}

export const UsersManagementModule = getModule(UsersManagementVuexModule);
